import { RemoteConfigProperties } from "../../../../models/featureFlags";
import { toApiField } from "./api";
import { ATTRIBUTION_MODEL_FIELDS } from "./configuration";
import { DELIVERY_MODEL_FIELDS } from "./delivery";
/**
 * Every flag the rules in this module read.
 *
 * A caller with the whole flag map to hand does not need this — the attribution
 * endpoints wrap the map and let each rule ask. A caller that has to resolve
 * flags one at a time does: the dashboard's `useFeatureFlags` takes its keys up
 * front. Declared here so that caller cannot fall behind a rule that starts
 * reading a new flag, which would leave it answering `false` for that flag
 * indefinitely while the service picked the rule up for free.
 *
 * Pinned by test against what the rules actually read, so this list cannot go
 * stale in either direction.
 */
export const ATTRIBUTION_POLICY_FLAGS = [
    RemoteConfigProperties.INSIGHTS_DASHBOARD_DAILY_SPEND,
    RemoteConfigProperties.INSIGHTS_DASHBOARD_FOOTFALL,
    RemoteConfigProperties.INSIGHTS_DASHBOARD_OVERLAP,
    RemoteConfigProperties.INSIGHTS_DASHBOARD_UNIVERSAL_PIXEL,
    RemoteConfigProperties.UNIVERSAL_PIXEL_CONVERSION_EVENTS,
    RemoteConfigProperties.UNIVERSAL_PIXEL_SELECTORS,
    RemoteConfigProperties.UNIVERSAL_PIXEL_VEHICLE_DETAILS
];
export const isUniversalPixelEnabled = (ctx) => ctx.isFlagEnabled(RemoteConfigProperties.INSIGHTS_DASHBOARD_UNIVERSAL_PIXEL) &&
    ctx.isFlagEnabled(RemoteConfigProperties.UNIVERSAL_PIXEL_SELECTORS);
export const isOverlapEnabled = (ctx) => ctx.isFlagEnabled(RemoteConfigProperties.INSIGHTS_DASHBOARD_OVERLAP);
export const isSpendVisible = (ctx) => ctx.isFlagEnabled(RemoteConfigProperties.INSIGHTS_DASHBOARD_DAILY_SPEND);
export const isFootfallEnabled = (ctx) => ctx.isFlagEnabled(RemoteConfigProperties.INSIGHTS_DASHBOARD_FOOTFALL);
export const areUpConversionEventsEnabled = (ctx) => ctx.isFlagEnabled(RemoteConfigProperties.UNIVERSAL_PIXEL_CONVERSION_EVENTS);
export const isVehicleDetailsEnabled = (ctx) => ctx.isFlagEnabled(RemoteConfigProperties.UNIVERSAL_PIXEL_VEHICLE_DETAILS);
/**
 * Whether a single field may be queried under this policy.
 *
 * Report type is deliberately not an argument: selecting several digital
 * channels at once must never withdraw Universal Pixel fields. Combine with the
 * caller's report types via {@link getAllowedAttributionFields} instead.
 *
 * `canUserQueryBy` is deliberately not read here. It marks dimensions the
 * dashboard manages on its tiles' behalf rather than offering in a dropdown;
 * an API caller may still ask for them, and `mediaType` has to be askable
 * for a channel breakdown to be requestable at all.
 */
export const isAttributionFieldAllowed = (field, ctx, options = {}) => {
    const config = ATTRIBUTION_MODEL_FIELDS[field];
    if (!config) {
        return false;
    }
    // `isSpendField`, not `unit: "currency"` — Universal Pixel purchase revenue is
    // dollar-denominated but is conversion value, not MadHive spend. The dashboard
    // gates on the same property.
    if (config.isSpendField && !isSpendVisible(ctx)) {
        return false;
    }
    if (config.isFootfallField && !isFootfallEnabled(ctx)) {
        return false;
    }
    if (config.isUniversalPixelField) {
        // Entitlement before scope. Testing the pixel type first would tell an
        // organization without Universal Pixel to set `pixel_type: UNIVERSAL`,
        // which is then refused — the wrong reason, and a 400 where the caller is
        // owed a 403.
        if (!isUniversalPixelEnabled(ctx)) {
            return false;
        }
        if (options.pixelType !== "UNIVERSAL") {
            return false;
        }
        if (config.isUpConversionEventField && !areUpConversionEventsEnabled(ctx)) {
            return false;
        }
        if (config.isVehicleDetailsField && !isVehicleDetailsEnabled(ctx)) {
            return false;
        }
    }
    return true;
};
/**
 * The subset of `fields` this policy permits, in the order given, as model keys.
 *
 * The counterpart to {@link getAllowedAttributionFields}, which enumerates
 * everything and answers in API names: a surface that already knows which
 * fields it wants — a dashboard tile's configured metric list, a saved tile
 * configuration — needs its own list narrowed rather than a fresh one built.
 */
export const filterAllowedAttributionFields = (fields, ctx, options = {}) => fields.filter((field) => isAttributionFieldAllowed(field, ctx, options));
/** Every field valid for these report types that this policy permits, as API names. */
export const getAllowedAttributionFields = (reportTypes, ctx, options = {}) => Object.keys(ATTRIBUTION_MODEL_FIELDS)
    .filter((field) => ATTRIBUTION_MODEL_FIELDS[field].reportTypes.some((type) => reportTypes.includes(type)))
    .filter((field) => isAttributionFieldAllowed(field, ctx, options))
    .map(toApiField);
/**
 * Every field valid for these report types regardless of policy, as API names.
 * The difference against {@link getAllowedAttributionFields} is what the
 * organization is being withheld, so a caller can be told "not available to
 * you" rather than "no such field".
 */
export const getQueryableAttributionFields = (reportTypes) => Object.keys(ATTRIBUTION_MODEL_FIELDS)
    .filter((field) => ATTRIBUTION_MODEL_FIELDS[field].reportTypes.some((type) => reportTypes.includes(type)))
    .map(toApiField);
/**
 * Every flag the rules in this module read for delivery.
 */
export const DELIVERY_POLICY_FLAGS = [
    RemoteConfigProperties.INSIGHTS_DASHBOARD_DAILY_SPEND
];
export const isDeliverySpendVisible = (ctx) => ctx.isFlagEnabled(RemoteConfigProperties.INSIGHTS_DASHBOARD_DAILY_SPEND);
/** Spend is the only entitlement a `DELIVERY_MODEL_FIELDS` field can be gated behind. */
export const isDeliveryFieldAllowed = (field, ctx) => !ATTRIBUTION_MODEL_FIELDS[field].isSpendField || isDeliverySpendVisible(ctx);
/** The subset of delivery fields this policy permits, as API names. */
export const getAllowedDeliveryFields = (ctx) => DELIVERY_MODEL_FIELDS.filter((field) => isDeliveryFieldAllowed(field, ctx)).map(toApiField);
//# sourceMappingURL=policy.js.map